import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
    AccessTimeOutlined,
    AddCircleOutline,
    AssignmentTurnedInOutlined,
    BadgeOutlined,
    CakeOutlined,
    EmailOutlined,
    FavoriteBorderOutlined,
    FavoriteOutlined,
    FitnessCenterOutlined,
    GroupsOutlined,
    HealthAndSafetyOutlined,
    HistoryOutlined,
    HomeOutlined,
    KeyboardArrowDown,
    KeyboardArrowUp,
    MoreVert,
    PersonOutline,
    PhoneOutlined,
    RestaurantOutlined,
    WarningAmberOutlined
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    ButtonBase,
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Tooltip,
    Typography
} from '@mui/material';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import WineBarIcon from '@mui/icons-material/WineBar';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getAlerts } from '../../../../services/alerts';
import { fetchAppointments } from '../../../../services/appointments';
import { genderOptions } from '../../Users/config/select';
import { getRelevantNutritionPlan } from './patientNutrition';

const formatDate = (value, includeTime = false) => {
    if (!value || !dayjs(value).isValid()) return null;
    return dayjs(value).format(includeTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
};

const SummaryItem = ({
    icon,
    label,
    value,
    loading = false,
    warning = false,
    onAction,
    actionLabel
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Box sx={{ display: 'flex', color: warning ? '#F5A623' : '#1976D2' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#263238', fontSize: '0.6875rem', fontWeight: 600 }}>
                {label}
            </Typography>
            {loading ? (
                <Skeleton width={92} height={18} />
            ) : (
                <Tooltip
                    title={onAction ? actionLabel : value}
                    disableHoverListener={
                        !onAction && (!value || String(value).length < 24)
                    }
                >
                    {onAction ? (
                        <ButtonBase
                            onClick={onAction}
                            aria-label={actionLabel}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                gap: 0.5,
                                minWidth: 0,
                                maxWidth: '100%',
                                borderRadius: 0.75,
                                color: '#1976D2',
                                textAlign: 'left',
                                '&:hover': { color: '#0A579B' },
                                '&:focus-visible': {
                                    outline: '2px solid #1976D2',
                                    outlineOffset: 2
                                }
                            }}
                        >
                            <Typography
                                noWrap
                                sx={{ fontSize: '0.75rem', fontWeight: 700 }}
                            >
                                {value}
                            </Typography>
                            <AddCircleOutline sx={{ flexShrink: 0, fontSize: 15 }} />
                        </ButtonBase>
                    ) : (
                        <Typography
                            noWrap
                            sx={{
                                color: '#60717B',
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }}
                        >
                            {value}
                        </Typography>
                    )}
                </Tooltip>
            )}
        </Box>
    </Box>
);

SummaryItem.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    loading: PropTypes.bool,
    warning: PropTypes.bool,
    onAction: PropTypes.func,
    actionLabel: PropTypes.string
};

const DetailItem = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', color: '#1976D2' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#87959D', fontSize: '0.6875rem' }}>
                {label}
            </Typography>
            <Typography
                noWrap
                sx={{ color: '#263238', fontSize: '0.75rem', fontWeight: 500 }}
            >
                {value ?? '—'}
            </Typography>
        </Box>
    </Box>
);

DetailItem.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const DetailSection = ({ title, children }) => (
    <Box>
        <Typography
            component="h2"
            sx={{ color: '#0A579B', fontSize: '0.8125rem', fontWeight: 700, mb: 1.25 }}
        >
            {title}
        </Typography>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                columnGap: 4,
                rowGap: 1.5
            }}
        >
            {children}
        </Box>
    </Box>
);

DetailSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};

const PatientHeader = ({
    patient,
    plans = [],
    groups = [],
    patientUuid,
    contextLoading,
    contextError,
    onEdit,
    onAddStressTest,
    onOpenMessages,
    onAssignPlan,
    onAssignGroup,
    nutritionPlans = [],
    nutritionLoading = false,
    nutritionError = false,
    onAssignNutrition
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const {
        data: upcomingAppointments = [],
        isPending: appointmentsLoading,
        isError: appointmentsError
    } = useQuery({
        queryKey: ['patient-header', patientUuid, 'next-appointment'],
        queryFn: () =>
            fetchAppointments({
                patient: patientUuid,
                timestamp: new Date().toISOString(),
                statuses: ['SCHEDULED'],
                order: 'ASC',
                pageLimit: 1
            }),
        enabled: Boolean(patientUuid)
    });

    const {
        data: alertsResponse,
        isPending: alertsLoading,
        isError: alertsError
    } = useQuery({
        queryKey: ['patient-header', patientUuid, 'latest-alert'],
        queryFn: () =>
            getAlerts({
                patient: patientUuid,
                qi: null,
                pageLimit: 1
            }),
        enabled: Boolean(patientUuid)
    });

    const activePlan = useMemo(() => {
        const today = dayjs();
        return plans
            .filter(
                (plan) =>
                    plan.accepted &&
                    !dayjs(plan.start_date).isAfter(today, 'day') &&
                    !dayjs(plan.end_date).isBefore(today, 'day')
            )
            .sort(
                (a, b) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf()
            )[0];
    }, [plans]);

    const pendingPlan = useMemo(() => {
        const today = dayjs();
        return plans
            .filter(
                (plan) =>
                    !plan.accepted &&
                    (!plan.end_date || !dayjs(plan.end_date).isBefore(today, 'day'))
            )
            .sort(
                (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
            )[0];
    }, [plans]);

    const upcomingPlan = useMemo(() => {
        const today = dayjs();
        return plans
            .filter(
                (plan) =>
                    plan.accepted &&
                    dayjs(plan.start_date).isAfter(today, 'day') &&
                    (!plan.end_date || !dayjs(plan.end_date).isBefore(today, 'day'))
            )
            .sort(
                (a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf()
            )[0];
    }, [plans]);

    const availableGroups = useMemo(() => {
        const today = dayjs();
        return groups
            .filter((group) => {
                const completed =
                    group.status === false ||
                    String(group.status || '').toUpperCase() === 'COMPLETED';
                const ended =
                    group.end_date && dayjs(group.end_date).isBefore(today, 'day');
                return !completed && !ended;
            })
            .sort((a, b) => {
                const aIsFuture = dayjs(a.start_date).isAfter(today, 'day');
                const bIsFuture = dayjs(b.start_date).isAfter(today, 'day');
                if (aIsFuture !== bIsFuture) return aIsFuture ? 1 : -1;
                return dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf();
            });
    }, [groups]);

    const nextAppointment = useMemo(
        () =>
            upcomingAppointments
                .filter(
                    (appointment) =>
                        appointment.status === 'SCHEDULED' &&
                        dayjs(appointment.ts).isAfter(dayjs())
                )
                .sort((a, b) => dayjs(a.ts).valueOf() - dayjs(b.ts).valueOf())[0],
        [upcomingAppointments]
    );

    const latestAlert = alertsResponse?.data?.[0];
    const assignedGroup = availableGroups[0];
    const nutritionPlan = getRelevantNutritionPlan(nutritionPlans);
    const nutritionValue = !nutritionPlan
        ? t('userPage.overview.noNutritionPlan')
        : !nutritionPlan.accepted
          ? t('userPage.overview.planPending', { name: nutritionPlan.name })
          : dayjs(nutritionPlan.start_date).isAfter(dayjs(), 'day')
            ? t('userPage.overview.planStarts', {
                  name: nutritionPlan.name,
                  date: formatDate(nutritionPlan.start_date)
              })
            : nutritionPlan.name;
    const planValue = activePlan?.name
        ? activePlan.name
        : pendingPlan?.name
          ? t('userPage.overview.planPending', { name: pendingPlan.name })
          : upcomingPlan?.name
            ? t('userPage.overview.planStarts', {
                  name: upcomingPlan.name,
                  date: formatDate(upcomingPlan.start_date)
              })
            : t('userPage.overview.noActivePlan');
    const groupValue = assignedGroup
        ? dayjs(assignedGroup.start_date).isAfter(dayjs(), 'day')
            ? t('userPage.overview.groupStarts', {
                  name: assignedGroup.name,
                  date: formatDate(assignedGroup.start_date)
              })
            : availableGroups.length > 1
              ? t('userPage.overview.additionalGroups', {
                    name: assignedGroup.name,
                    count: availableGroups.length - 1
                })
              : assignedGroup.name
        : t('userPage.overview.noGroup');
    const closeMenu = () => setAnchorEl(null);
    const runAction = (action) => {
        closeMenu();
        action();
    };

    const fullAddress = [
        patient?.address?.street,
        patient?.address?.city,
        patient?.address?.zip
    ]
        .filter(Boolean)
        .join(', ');

    const sexMap = { FEMALE: 0, MALE: 1, OTHER: 2, NB_INTERSEX: 2 };
    const genderMap = {
        WOMAN: 0,
        MAN: 1,
        TRANS_WOMAN: 2,
        TRANS_MAN: 3,
        NON_BINARY: 4,
        NONBINARY: 4,
        AGENDER: 5,
        GENDERQUEER: 6,
        PREFER_NOT_TO_SAY: 7,
        PREFER_NO_ANSWER: 7,
        NOT_LISTED: 8
    };
    const sexIndex = sexMap[patient?.sex];
    const sex = patient?.sex
        ? sexIndex !== undefined
            ? t(`sexOptions.${sexIndex}.name`)
            : patient.sex
        : null;
    const gender = patient?.gender
        ? genderOptions[genderMap[patient.gender]]?.name || patient.gender
        : null;

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 1.5,
                borderColor: '#E0E5E9',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: '#1976D2', mr: 2 }}>
                        {patient?.name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            minWidth: 0
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{
                                color: '#101418',
                                fontSize: '1.25rem',
                                fontWeight: 700,
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {patient?.name || t('userPage.defaultName')}
                        </Typography>
                        {patient?.user_data?.smoking === true && (
                            <Tooltip title={t('users.fields.smoking')}>
                                <Box
                                    component="span"
                                    role="img"
                                    aria-label={t('users.fields.smoking')}
                                    tabIndex={0}
                                    sx={{
                                        display: 'inline-flex',
                                        flexShrink: 0,
                                        color: '#60717B'
                                    }}
                                >
                                    <SmokingRoomsIcon fontSize="small" />
                                </Box>
                            </Tooltip>
                        )}
                        {patient?.user_data?.alcoholConsumption === true && (
                            <Tooltip title={t('users.fields.alcoholConsumption')}>
                                <Box
                                    component="span"
                                    role="img"
                                    aria-label={t('users.fields.alcoholConsumption')}
                                    tabIndex={0}
                                    sx={{
                                        display: 'inline-flex',
                                        flexShrink: 0,
                                        color: '#60717B'
                                    }}
                                >
                                    <WineBarIcon fontSize="small" />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={t('userPage.overview.patientActions')}>
                        <IconButton
                            size="small"
                            aria-label={t('userPage.overview.patientActions')}
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                        >
                            <MoreVert fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('userPage.overview.togglePatientDetails')}>
                        <IconButton
                            size="small"
                            aria-label={t('userPage.overview.togglePatientDetails')}
                            aria-expanded={expanded}
                            onClick={() => setExpanded((current) => !current)}
                        >
                            {expanded ? (
                                <KeyboardArrowUp fontSize="small" />
                            ) : (
                                <KeyboardArrowDown fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={closeMenu}
                    >
                        <MenuItem onClick={() => runAction(onEdit)}>
                            {t('userPage.editPatientData')}
                        </MenuItem>
                        <MenuItem onClick={() => runAction(onAddStressTest)}>
                            {t('userPage.addStressTest')}
                        </MenuItem>
                        <MenuItem onClick={() => runAction(onOpenMessages)}>
                            {t('userPage.openMessages')}
                        </MenuItem>
                    </Menu>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(3, minmax(0, 1fr))',
                            xl: 'repeat(6, minmax(0, 1fr))'
                        },
                        gap: 2,
                        px: 5
                    }}
                >
                    <SummaryItem
                        icon={<AssignmentTurnedInOutlined />}
                        label={t('userPage.overview.trainingPlan')}
                        value={
                            contextError ? t('userPage.overview.unavailable') : planValue
                        }
                        loading={contextLoading}
                        onAction={
                            !contextLoading &&
                            !contextError &&
                            !activePlan &&
                            !pendingPlan &&
                            !upcomingPlan
                                ? onAssignPlan
                                : undefined
                        }
                        actionLabel={t('userPage.overview.assignPlan')}
                    />
                    <SummaryItem
                        icon={<RestaurantOutlined />}
                        label={t('userPage.overview.nutritionPlan')}
                        value={
                            nutritionError
                                ? t('userPage.overview.unavailable')
                                : nutritionValue
                        }
                        loading={nutritionLoading}
                        onAction={
                            !nutritionLoading && !nutritionError && !nutritionPlan
                                ? onAssignNutrition
                                : undefined
                        }
                        actionLabel={t(
                            'userPage.overview.assignments.nutritionDialogTitle'
                        )}
                    />
                    <SummaryItem
                        icon={<GroupsOutlined />}
                        label={t('userPage.overview.group')}
                        value={
                            contextError ? t('userPage.overview.unavailable') : groupValue
                        }
                        loading={contextLoading}
                        onAction={
                            !contextLoading && !contextError && !assignedGroup
                                ? onAssignGroup
                                : undefined
                        }
                        actionLabel={t('userPage.overview.assignGroup')}
                    />
                    <SummaryItem
                        icon={<HistoryOutlined />}
                        label={t('userPage.overview.lastAccess')}
                        value={
                            contextError
                                ? t('userPage.overview.unavailable')
                                : formatDate(patient?.last_access, true) ||
                                  t('userPage.overview.noAccess')
                        }
                        loading={contextLoading}
                    />
                    <SummaryItem
                        icon={<AccessTimeOutlined />}
                        label={t('userPage.overview.nextConsultation')}
                        value={
                            appointmentsError
                                ? t('userPage.overview.unavailable')
                                : formatDate(nextAppointment?.ts) ||
                                  t('userPage.overview.noConsultationScheduled')
                        }
                        loading={appointmentsLoading}
                    />
                    <SummaryItem
                        icon={<WarningAmberOutlined />}
                        label={t('userPage.overview.latestAlert')}
                        value={
                            alertsError
                                ? t('userPage.overview.unavailable')
                                : formatDate(latestAlert?.ts) ||
                                  t('userPage.overview.noAlerts')
                        }
                        loading={alertsLoading}
                        warning={Boolean(latestAlert)}
                    />
                </Box>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'grid', gap: 2.25 }}>
                        <DetailSection title={t('userPage.contactAndIdentification')}>
                            <DetailItem
                                icon={<EmailOutlined fontSize="small" />}
                                label={t('userPage.email')}
                                value={patient?.email}
                            />
                            <DetailItem
                                icon={<PhoneOutlined fontSize="small" />}
                                label={t('userPage.phone')}
                                value={patient?.phone}
                            />
                            <DetailItem
                                icon={<HomeOutlined fontSize="small" />}
                                label={t('userPage.address')}
                                value={fullAddress || null}
                            />
                            <DetailItem
                                icon={<CakeOutlined fontSize="small" />}
                                label={t('userPage.birthDate')}
                                value={formatDate(patient?.birth_date)}
                            />
                            <DetailItem
                                icon={<BadgeOutlined fontSize="small" />}
                                label={t('userPage.sex')}
                                value={sex}
                            />
                            <DetailItem
                                icon={<PersonOutline fontSize="small" />}
                                label={t('userPage.gender')}
                                value={gender}
                            />
                        </DetailSection>

                        <DetailSection title={t('userPage.adminData')}>
                            <DetailItem
                                icon={<HealthAndSafetyOutlined fontSize="small" />}
                                label={t('userPage.healthNumber')}
                                value={patient?.health_id}
                            />
                            <DetailItem
                                icon={<HealthAndSafetyOutlined fontSize="small" />}
                                label={t('userPage.healthNumberType')}
                                value={patient?.health_id_type}
                            />
                            <DetailItem
                                icon={<AccessTimeOutlined fontSize="small" />}
                                label={t('userPage.lastAccess')}
                                value={formatDate(patient?.last_access, true)}
                            />
                        </DetailSection>

                        <DetailSection title={t('userPage.healthData')}>
                            <DetailItem
                                icon={<FavoriteOutlined fontSize="small" />}
                                label={t('userPage.maxHR')}
                                value={patient?.user_data?.maxHeartRate}
                            />
                            <DetailItem
                                icon={<FavoriteBorderOutlined fontSize="small" />}
                                label={t('userPage.restingHR')}
                                value={patient?.user_data?.restingHeartRate}
                            />
                            <DetailItem
                                icon={<FitnessCenterOutlined fontSize="small" />}
                                label={t('userPage.trainingHR')}
                                value={patient?.user_data?.trainingHeartRate}
                            />
                        </DetailSection>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

PatientHeader.propTypes = {
    patient: PropTypes.object,
    plans: PropTypes.arrayOf(PropTypes.object),
    groups: PropTypes.arrayOf(PropTypes.object),
    patientUuid: PropTypes.string.isRequired,
    contextLoading: PropTypes.bool.isRequired,
    contextError: PropTypes.bool.isRequired,
    onEdit: PropTypes.func.isRequired,
    onAddStressTest: PropTypes.func.isRequired,
    onOpenMessages: PropTypes.func.isRequired,
    onAssignPlan: PropTypes.func.isRequired,
    onAssignGroup: PropTypes.func.isRequired,
    nutritionPlans: PropTypes.arrayOf(PropTypes.object),
    nutritionLoading: PropTypes.bool,
    nutritionError: PropTypes.bool,
    onAssignNutrition: PropTypes.func
};

export default PatientHeader;
